import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { storeAwareness, onBindingChange } from '../store/canvas-store';
import type { Awareness } from '../store/ydoc/provider';
import { colorFor, handleFor, initialsFor } from './identity';
import { createRafThrottle, createRafBatch, type RafThrottle } from './throttle';
import { peersFromStates, type AwarenessLocalState, type PresencePeer } from './types';

export interface PresenceApi {
  self: PresencePeer | null;
  others: PresencePeer[];
  /** Publish the local cursor in CANVAS coords (throttled ≤30Hz, rAF-batched). */
  setCursor: (x: number, y: number) => void;
  clearCursor: () => void;
  /** Publish the local frame selection. */
  setSelection: (frameId: string | undefined) => void;
  /** Publish the local viewport (for follow-mode). */
  setViewport: (vp: PresencePeer['viewport']) => void;
  /** Publish (or clear, with null) the agent pseudo-peer — used by 3.3. */
  publishAgent: (peer: PresencePeer | null) => void;
}

/** Identity for the signed-in user. `null` in local dev (no email). */
function useSelf(): PresencePeer | null {
  const email = useAuth((s) => s.email);
  return useMemo<PresencePeer | null>(
    () => (email
      ? { id: email, name: handleFor(email), color: colorFor(email), initials: initialsFor(email), isAgent: false }
      : null),
    [email],
  );
}

/**
 * Awareness-backed presence. Publishes the local user's identity + cursor +
 * selection into Yjs Awareness and exposes the other live peers. Cursor writes
 * are throttled; identity/selection are immediate. Returns inert no-ops when
 * there's no awareness (local/single-user mode) or no identity.
 */
export function usePresence(): PresenceApi {
  const self = useSelf();
  const [others, setOthers] = useState<PresencePeer[]>([]);
  const awarenessRef = useRef<Awareness | null>(null);
  const localRef = useRef<PresencePeer | null>(null);
  const throttleRef = useRef<RafThrottle<{ x: number; y: number }> | null>(null);

  // Push the current local presence object into awareness.
  const publish = (): void => {
    const aw = awarenessRef.current;
    if (aw && localRef.current) aw.setLocalStateField('presence', localRef.current);
  };

  useEffect(() => {
    let disposed = false;

    const readPeers = (aw: Awareness): void => {
      const states = aw.getStates() as Map<number, AwarenessLocalState>;
      setOthers(peersFromStates(states, aw.clientID));
    };

    let detach: (() => void) | null = null;
    const attach = (): void => {
      detach?.();
      detach = null;
      const aw = storeAwareness();
      awarenessRef.current = aw;
      if (!aw) { setOthers([]); return; }
      // seed identity (cursor/selection arrive later)
      localRef.current = self ? { ...self } : null;
      publish();
      // Coalesce bursts of remote awareness changes into one render per frame.
      const batch = createRafBatch(() => { if (!disposed) readPeers(aw); });
      const onChange = (): void => { batch.schedule(); };
      aw.on('change', onChange);
      readPeers(aw);
      detach = () => { aw.off('change', onChange); batch.cancel(); };
    };

    attach();
    const unbind = onBindingChange(attach); // re-attach when load() rebinds

    return () => {
      disposed = true;
      unbind();
      detach?.();
      throttleRef.current?.cancel();
      awarenessRef.current?.setLocalStateField('presence', null as unknown as PresencePeer);
    };
    // self identity is stable per session; re-seed if it changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [self]);

  // Lazily build the cursor throttle bound to the latest publish closure.
  if (!throttleRef.current) {
    throttleRef.current = createRafThrottle<{ x: number; y: number }>((c) => {
      if (localRef.current) { localRef.current = { ...localRef.current, cursor: c }; publish(); }
    });
  }

  return {
    self,
    others,
    setCursor: (x, y) => throttleRef.current!.push({ x, y }),
    clearCursor: () => {
      throttleRef.current!.cancel();
      if (localRef.current?.cursor) { localRef.current = { ...localRef.current, cursor: undefined }; publish(); }
    },
    setSelection: (frameId) => {
      if (!localRef.current) return;
      localRef.current = { ...localRef.current, selection: frameId };
      publish();
    },
    setViewport: (vp) => {
      if (!localRef.current) return;
      localRef.current = { ...localRef.current, viewport: vp };
      publish();
    },
    publishAgent: (peer) => {
      awarenessRef.current?.setLocalStateField('agent', peer as unknown as PresencePeer);
    },
  };
}
